/**
 * Utilitário centralizado para resolução de acesso a features
 * Usado por useUserFeatures (menu) e FeatureGuard (páginas)
 */

import { StoreFeature } from '@/types/networkUser';
import { FEATURE_SUBFEATURES, isValidSubFeature, SubFeature } from '@/types/subFeatures';

// Mapeamento reverso: valores do banco → valores do frontend
const FEATURE_REVERSE_MAPPING: Record<string, StoreFeature> = {
  'CLIENTES': StoreFeature.PESSOAS,
  'EQUIPE': StoreFeature.GERENCIAR,
};

/**
 * Normaliza features do banco para valores do frontend
 */
function normalizeFeature(feature: string): StoreFeature {
  return FEATURE_REVERSE_MAPPING[feature] || feature as StoreFeature;
}

// Resultado estruturado para evitar ambiguidades
export type FeatureAccessResult = 
  | { mode: 'ALL' }                                    // Acesso total (NETWORK_ADMIN ou features NULL)
  | { mode: 'RESTRICTED'; features: StoreFeature[] }  // Acesso restrito às features listadas
  | { mode: 'NONE' };                                  // Sem acesso

// Resultado estruturado para sub-features
export type SubFeatureAccessResult =
  | { mode: 'ALL' }                                    // Acesso total a todas sub-features
  | { mode: 'RESTRICTED'; subFeatures: string[] }     // Acesso restrito às sub-features listadas
  | { mode: 'NONE' };                                  // Sem acesso

export interface UserRole {
  role: string;
  features: string[] | null;
  sub_features: string[] | null;
  scope_type: string | null;
  store_ids: string[] | null;
}

/**
 * Features que suportam visão consolidada (modo NETWORK sem loja selecionada)
 * Dados serão consolidados apenas das lojas onde o usuário tem acesso à feature
 * 
 * FINANCEIRO não está incluído pois cada loja tem caixa/contas separados
 */
export const FEATURES_SUPPORT_NETWORK_MODE: StoreFeature[] = [
  StoreFeature.INICIO,
  StoreFeature.DASHBOARD,
  StoreFeature.CLIENTES,
  StoreFeature.VENDAS,
  StoreFeature.ESTOQUE,
  StoreFeature.MARKETING,
  StoreFeature.TAREFAS,
  StoreFeature.DOCUMENTACAO,
  StoreFeature.AUDITORIA,
  StoreFeature.FERRAMENTAS,
  StoreFeature.CONFIGURACOES,
  StoreFeature.EQUIPE,
];

/**
 * Resolve as features efetivas do usuário baseado em suas roles
 * @param roles - Array de roles do usuário na rede
 * @param storeId - ID da loja atual (null se modo NETWORK)
 * @param contextMode - Modo do contexto ('NETWORK' ou 'STORE')
 */
export function resolveUserFeatures(
  roles: UserRole[],
  storeId: string | null,
  contextMode: 'NETWORK' | 'STORE'
): FeatureAccessResult {
  console.log('🔍 resolveUserFeatures - entrada:', { rolesCount: roles.length, storeId, contextMode });
  
  if (!roles || roles.length === 0) {
    console.log('⚠️ resolveUserFeatures - sem roles, sem acesso');
    return { mode: 'NONE' };
  }
  
  // 1. Verificar se tem role de NETWORK_ADMIN (acesso total)
  if (roles.some(r => r.role === 'NETWORK_ADMIN')) {
    console.log('✅ resolveUserFeatures - NETWORK_ADMIN detectado, acesso total');
    return { mode: 'ALL' };
  }
  
  // 2. Filtrar roles que dão acesso ao contexto atual
  const relevantRoles = filterRelevantRoles(roles, storeId, contextMode);
  
  console.log('🔍 resolveUserFeatures - roles relevantes:', relevantRoles.length);
  
  if (relevantRoles.length === 0) {
    console.log('⚠️ resolveUserFeatures - nenhuma role relevante para o contexto');
    return { mode: 'NONE' };
  }
  
  // 3. Verificar se alguma role tem features NULL (acesso total)
  if (relevantRoles.some(r => r.features === null)) {
    console.log('✅ resolveUserFeatures - role com features NULL, acesso total');
    return { mode: 'ALL' };
  }
  
  // 4. Combinar features únicas de todas as roles relevantes (com normalização)
  const allFeatures = new Set<StoreFeature>();
  for (const role of relevantRoles) {
    if (role.features && Array.isArray(role.features)) {
      role.features.forEach((f: string) => allFeatures.add(normalizeFeature(f)));
    }
  }
  
  const featuresArray = Array.from(allFeatures);
  console.log('✅ resolveUserFeatures - features finais:', featuresArray);
  
  return { 
    mode: 'RESTRICTED', 
    features: featuresArray 
  };
}

/**
 * Filtra roles que são relevantes para o contexto atual
 */
function filterRelevantRoles(
  roles: UserRole[],
  storeId: string | null,
  contextMode: 'NETWORK' | 'STORE'
): UserRole[] {
  // Em modo NETWORK sem loja selecionada, considerar todas as roles
  if (contextMode === 'NETWORK' && !storeId) {
    return roles;
  }
  
  // Em modo STORE ou com loja selecionada, filtrar por acesso à loja
  if (!storeId) {
    return roles;
  }
  
  return roles.filter(r => {
    // ALL_STORES dá acesso a qualquer loja
    if (r.scope_type === 'ALL_STORES') return true;
    // STORE_SET ou SINGLE_STORE verifica store_ids
    if (r.store_ids && r.store_ids.includes(storeId)) return true;
    return false;
  });
}

/**
 * Verifica se o usuário tem acesso a uma feature específica
 */
export function hasFeatureInResult(
  result: FeatureAccessResult,
  feature: StoreFeature
): boolean {
  switch (result.mode) {
    case 'ALL':
      return true;
    case 'NONE':
      return false;
    case 'RESTRICTED':
      return result.features.includes(feature);
  }
}

/**
 * Verifica se uma feature requer seleção de loja
 */
export function featureRequiresStore(feature: StoreFeature): boolean {
  return !FEATURES_SUPPORT_NETWORK_MODE.includes(feature);
}

/**
 * Resolve as sub-features efetivas do usuário baseado em suas roles
 * @param roles - Array de roles do usuário na rede
 * @param storeId - ID da loja atual (null se modo NETWORK)
 * @param contextMode - Modo do contexto ('NETWORK' ou 'STORE')
 * @param enabledFeatures - Features habilitadas para o usuário (para fallback)
 */
export function resolveUserSubFeatures(
  roles: UserRole[],
  storeId: string | null,
  contextMode: 'NETWORK' | 'STORE',
  enabledFeatures: FeatureAccessResult
): SubFeatureAccessResult {
  console.log('🔍 resolveUserSubFeatures - entrada:', { rolesCount: roles.length, storeId, contextMode });
  
  if (!roles || roles.length === 0) {
    console.log('⚠️ resolveUserSubFeatures - sem roles, sem acesso');
    return { mode: 'NONE' };
  }
  
  // 1. Verificar se tem role de NETWORK_ADMIN (acesso total)
  if (roles.some(r => r.role === 'NETWORK_ADMIN')) {
    console.log('✅ resolveUserSubFeatures - NETWORK_ADMIN detectado, acesso total');
    return { mode: 'ALL' };
  }
  
  // 2. Filtrar roles que dão acesso ao contexto atual
  const relevantRoles = filterRelevantRoles(roles, storeId, contextMode);
  
  console.log('🔍 resolveUserSubFeatures - roles relevantes:', relevantRoles.length);
  
  if (relevantRoles.length === 0) {
    console.log('⚠️ resolveUserSubFeatures - nenhuma role relevante para o contexto');
    return { mode: 'NONE' };
  }
  
  // 3. Verificar se alguma role tem features NULL (acesso total) - implica sub-features totais também
  if (relevantRoles.some(r => r.features === null)) {
    console.log('✅ resolveUserSubFeatures - role com features NULL, acesso total');
    return { mode: 'ALL' };
  }
  
  // 4. Coletar todas as sub_features explícitas das roles relevantes
  const explicitSubFeatures = new Set<string>();
  let hasExplicitSubFeatures = false;
  
  for (const role of relevantRoles) {
    if (role.sub_features && Array.isArray(role.sub_features) && role.sub_features.length > 0) {
      hasExplicitSubFeatures = true;
      role.sub_features.forEach((sf: string) => explicitSubFeatures.add(sf));
    }
  }
  
  // 5. Se há sub-features explícitas, usar apenas elas
  if (hasExplicitSubFeatures) {
    const subFeaturesArray = Array.from(explicitSubFeatures);
    console.log('✅ resolveUserSubFeatures - sub-features explícitas:', subFeaturesArray);
    return { 
      mode: 'RESTRICTED', 
      subFeatures: subFeaturesArray 
    };
  }
  
  // 6. Fallback: se não há sub-features explícitas, habilitar TODAS as sub-features das features habilitadas
  // Isso mantém compatibilidade com usuários antigos que não têm sub_features configuradas
  if (enabledFeatures.mode === 'ALL') {
    console.log('✅ resolveUserSubFeatures - sem sub-features explícitas + features ALL, acesso total');
    return { mode: 'ALL' };
  }
  
  if (enabledFeatures.mode === 'RESTRICTED') {
    const allSubFeaturesFromFeatures = new Set<string>();
    enabledFeatures.features.forEach(feature => {
      const moduleSubFeatures = FEATURE_SUBFEATURES[feature] || [];
      moduleSubFeatures.forEach(sf => allSubFeaturesFromFeatures.add(sf));
    });
    const subFeaturesArray = Array.from(allSubFeaturesFromFeatures);
    console.log('✅ resolveUserSubFeatures - fallback: todas sub-features das features:', subFeaturesArray);
    return {
      mode: 'RESTRICTED',
      subFeatures: subFeaturesArray
    };
  }
  
  return { mode: 'NONE' };
}

/**
 * Verifica se o usuário tem acesso a uma sub-feature específica
 */
export function hasSubFeatureInResult(
  result: SubFeatureAccessResult,
  subFeatureId: string
): boolean {
  switch (result.mode) {
    case 'ALL':
      return true;
    case 'NONE':
      return false;
    case 'RESTRICTED':
      return result.subFeatures.includes(subFeatureId);
  }
}
